import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Send, FileText, Car, MapPin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Separator } from "@/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface EmailTemplate {
  id?: string;
  type: 'transfer' | 'tour';
  subject: string;
  html_content: string;
  variables: string[];
  is_active: boolean;
}

const transferVariables = [
  'booking_number', 
  'customer_name', 
  'email', 
  'phone', 
  'from_location', 
  'to_location', 
  'passengers', 
  'trip_type', 
  'service_type', 
  'total_price'
];

const tourVariables = [
  'booking_number', 
  'customer_name', 
  'email', 
  'phone', 
  'tour_name', 
  'booking_date', 
  'tour_schedule', 
  'pickup_point', 
  'adults', 
  'total_price'
];

const defaultTransferTemplate: EmailTemplate = {
  type: 'transfer',
  subject: 'Confirmación de Transfer - {{booking_number}}',
  html_content: `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
        <h1 style="margin: 0; font-size: 28px;">¡Transfer Confirmado!</h1>
        <p style="margin: 10px 0 0 0; font-size: 16px;">Número de reserva: {{booking_number}}</p>
      </div>
      
      <div style="background: white; padding: 30px; border: 1px solid #e5e5e5; border-radius: 0 0 10px 10px;">
        <h2 style="color: #333; margin-top: 0;">Detalles del Transfer</h2>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Cliente:</td>
            <td style="padding: 10px 0;">{{customer_name}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Email:</td>
            <td style="padding: 10px 0;">{{email}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Teléfono:</td>
            <td style="padding: 10px 0;">{{phone}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Desde:</td>
            <td style="padding: 10px 0;">{{from_location}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Hasta:</td>
            <td style="padding: 10px 0;">{{to_location}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Pasajeros:</td>
            <td style="padding: 10px 0;">{{passengers}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Tipo de Viaje:</td>
            <td style="padding: 10px 0;">{{trip_type}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Tipo de Servicio:</td>
            <td style="padding: 10px 0;">{{service_type}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Precio Total:</td>
            <td style="padding: 10px 0; color: #667eea; font-weight: bold; font-size: 18px;">$\{{total_price\}}</td>
          </tr>
        </table>
        
        <div style="background: #f8f9ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Información Importante</h3>
          <p style="margin: 10px 0;">• Por favor llegue 15 minutos antes de la hora programada</p>
          <p style="margin: 10px 0;">• Tenga a mano su número de reserva: <strong>{{booking_number}}</strong></p>
          <p style="margin: 10px 0;">• En caso de dudas, contacte nuestro soporte</p>
        </div>
        
        <div style="text-align: center; margin-top: 30px;">
          <p style="color: #666;">¡Gracias por confiar en nosotros!</p>
        </div>
      </div>
    </div>
  `,
  variables: transferVariables,
  is_active: true
};

const defaultTourTemplate: EmailTemplate = {
  type: 'tour',
  subject: 'Confirmación de Tour - {{booking_number}}',
  html_content: `
    <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
      <div style="background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%); color: #333; padding: 30px; text-align: center; border-radius: 10px 10px 0 0;">
        <h1 style="margin: 0; font-size: 28px;">¡Tour Confirmado!</h1>
        <p style="margin: 10px 0 0 0; font-size: 16px;">Número de reserva: {{booking_number}}</p>
      </div>
      
      <div style="background: white; padding: 30px; border: 1px solid #e5e5e5; border-radius: 0 0 10px 10px;">
        <h2 style="color: #333; margin-top: 0;">Detalles del Tour</h2>
        
        <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Cliente:</td>
            <td style="padding: 10px 0;">{{customer_name}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Email:</td>
            <td style="padding: 10px 0;">{{email}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Teléfono:</td>
            <td style="padding: 10px 0;">{{phone}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Tour:</td>
            <td style="padding: 10px 0;">{{tour_name}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Fecha:</td>
            <td style="padding: 10px 0;">{{booking_date}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Horario:</td>
            <td style="padding: 10px 0;">{{tour_schedule}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Punto de Recogida:</td>
            <td style="padding: 10px 0;">{{pickup_point}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Adultos:</td>
            <td style="padding: 10px 0;">{{adults}}</td>
          </tr>
          <tr style="border-bottom: 1px solid #eee;">
            <td style="padding: 10px 0; font-weight: bold;">Precio Total:</td>
            <td style="padding: 10px 0; color: #fcb69f; font-weight: bold; font-size: 18px;">$\{{total_price\}}</td>
          </tr>
        </table>
        
        <div style="background: #fff7ed; padding: 20px; border-radius: 8px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Información Importante</h3>
          <p style="margin: 10px 0;">• Por favor llegue 15 minutos antes de la hora programada</p>
          <p style="margin: 10px 0;">• Tenga a mano su número de reserva: <strong>{{booking_number}}</strong></p>
          <p style="margin: 10px 0;">• Traiga ropa cómoda y protector solar</p>
          <p style="margin: 10px 0;">• En caso de dudas, contacte nuestro soporte</p>
        </div>
        
        <div style="text-align: center; margin-top: 30px;">
          <p style="color: #666;">¡Esperamos que disfrute su experiencia!</p>
        </div>
      </div>
    </div>
  `,
  variables: tourVariables,
  is_active: true
};

const AdminEmailTemplates = () => {
  const [transferTemplate, setTransferTemplate] = useState<EmailTemplate>(defaultTransferTemplate);
  const [tourTemplate, setTourTemplate] = useState<EmailTemplate>(defaultTourTemplate);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingSend, setTestingSend] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from('site_config')
        .select('*')
        .in('key', ['email_template_transfer', 'email_template_tour']);

      if (error) throw error;

      if (data) {
        const transferConfig = data.find(t => t.key === 'email_template_transfer');
        const tourConfig = data.find(t => t.key === 'email_template_tour');

        if (transferConfig && transferConfig.value) {
          const transferData = transferConfig.value as any;
          setTransferTemplate({
            id: transferConfig.id,
            type: 'transfer',
            subject: transferData.subject || defaultTransferTemplate.subject,
            html_content: transferData.html_content || defaultTransferTemplate.html_content,
            variables: transferData.variables || defaultTransferTemplate.variables,
            is_active: transferData.is_active !== undefined ? transferData.is_active : defaultTransferTemplate.is_active
          });
        }
        if (tourConfig && tourConfig.value) {
          const tourData = tourConfig.value as any;
          setTourTemplate({
            id: tourConfig.id,
            type: 'tour',
            subject: tourData.subject || defaultTourTemplate.subject,
            html_content: tourData.html_content || defaultTourTemplate.html_content,
            variables: tourData.variables || defaultTourTemplate.variables,
            is_active: tourData.is_active !== undefined ? tourData.is_active : defaultTourTemplate.is_active
          });
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (template: EmailTemplate) => {
    setSaving(true);
    try {
      const configKey = template.type === 'transfer' ? 'email_template_transfer' : 'email_template_tour';
      
      const { error } = await supabase
        .from('site_config')
        .upsert({
          key: configKey,
          value: {
            type: template.type,
            subject: template.subject,
            html_content: template.html_content,
            variables: template.variables,
            is_active: template.is_active
          },
          description: `Template de email para ${template.type === 'transfer' ? 'transfers' : 'tours'}`
        }, {
          onConflict: 'key'
        });

      if (error) throw error;

      toast({
        title: "Template guardado",
        description: `Template de ${template.type === 'transfer' ? 'transfer' : 'tour'} guardado correctamente`,
      });

      fetchTemplates();
    } catch (error: any) {
      console.error('Error saving template:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Error al guardar el template",
      });
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async (template: EmailTemplate) => {
    setTestingSend(template.type);
    try {
      const { error } = await supabase.functions.invoke('send-test-template', {
        body: {
          template_type: template.type,
          template_data: template
        }
      });

      if (error) throw error;

      toast({
        title: "Email de prueba enviado",
        description: "Revisa tu bandeja de entrada",
      });
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Error al enviar el email de prueba",
      });
    } finally {
      setTestingSend(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const TemplateForm = ({ template, setTemplate, type }: {
    template: EmailTemplate;
    setTemplate: (template: EmailTemplate) => void;
    type: 'transfer' | 'tour';
  }) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {type === 'transfer' ? <Car className="h-5 w-5" /> : <MapPin className="h-5 w-5" />}
          Template de {type === 'transfer' ? 'Transfer' : 'Tour'}
        </CardTitle>
        <CardDescription>
          Personaliza el voucher que se enviará a los clientes cuando reserven un {type === 'transfer' ? 'transfer' : 'tour'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor={`${type}-subject`}>Asunto del Email</Label>
          <Input
            id={`${type}-subject`}
            value={template.subject}
            onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
            placeholder={`Asunto del email de ${type}`}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${type}-content`}>Contenido HTML</Label>
          <Textarea
            id={`${type}-content`}
            value={template.html_content}
            onChange={(e) => setTemplate({ ...template, html_content: e.target.value })}
            rows={15}
            className="font-mono text-sm"
            placeholder="Contenido HTML del template..."
          />
        </div>

        <div className="space-y-2">
          <Label>Variables Disponibles</Label>
          <div className="p-3 bg-muted rounded-md">
            <p className="text-sm text-muted-foreground mb-2">
              Usa estas variables en tu template (incluye las llaves dobles):
            </p>
            <div className="flex flex-wrap gap-2">
              {template.variables.map((variable) => (
                <code key={variable} className="px-2 py-1 bg-background rounded text-xs">
                  {`{{${variable}}}`}
                </code>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id={`${type}-active`}
            checked={template.is_active}
            onCheckedChange={(checked) => setTemplate({ ...template, is_active: checked })}
          />
          <Label htmlFor={`${type}-active`}>Template activo</Label>
        </div>

        <Separator />

        <div className="flex gap-2">
          <Button
            onClick={() => saveTemplate(template)}
            disabled={saving}
            className="flex-1"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar Template
          </Button>
          <Button
            variant="outline"
            onClick={() => sendTestEmail(template)}
            disabled={testingSend === type}
          >
            {testingSend === type ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Enviar Prueba
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="h-8 w-8" />
            Templates de Email
          </h1>
          <p className="text-muted-foreground">
            Gestiona los vouchers que se envían a los clientes tras realizar una reserva
          </p>
        </div>

        <Tabs defaultValue="transfer" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="transfer" className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              Templates Transfer
            </TabsTrigger>
            <TabsTrigger value="tour" className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Templates Tour
            </TabsTrigger>
          </TabsList>

          <TabsContent value="transfer" className="space-y-6">
            <TemplateForm
              template={transferTemplate}
              setTemplate={setTransferTemplate}
              type="transfer"
            />
          </TabsContent>

          <TabsContent value="tour" className="space-y-6">
            <TemplateForm
              template={tourTemplate}
              setTemplate={setTourTemplate}
              type="tour"
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminEmailTemplates;